import { useState, useCallback } from 'react';
import { ChatAPIService, Message } from '../../services/chatService';
import { createTemporaryMessage } from './helpers';

/* eslint-disable max-lines-per-function */
/**
 * Hook para operações de mutação do chat
 */
export const useChatMutations = (userId?: string, userName?: string, onSuccess?: () => void) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assumeConversation = useCallback(
    async (conversationId: string) => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);
        await ChatAPIService.assumeConversation(conversationId, userId, userName);
        onSuccess?.();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erro ao assumir conversa';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userId, userName, onSuccess]
  );

  const returnToBot = useCallback(
    async (conversationId: string) => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);
        await ChatAPIService.returnToBot(conversationId);
        onSuccess?.();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erro ao devolver conversa ao bot';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userId, onSuccess]
  );

  const sendMessage = useCallback(
    async (conversationId: string, message: string): Promise<Message> => {
      if (!userId) throw new Error('User not authenticated');
      try {
        setError(null);
        await ChatAPIService.sendMessage(conversationId, message, userId, userName);
        return createTemporaryMessage(message);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao enviar mensagem';
        setError(errorMessage);
        throw err;
      }
    },
    [userId, userName]
  );

  return {
    loading,
    error,
    assumeConversation,
    returnToBot,
    sendMessage,
  };
};
